import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import NoResultsMessage from "../../algolia_search/no_results_message";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";

import EmptyResults from "./EmptyResults";
import PrCardGrid from "../card/PrCardGrid";
import PrCardList from "../card/PrCardList";
import prListActionColumnWidth from "../card/listActionColumn";
import { listHeaderRowStyle } from "../../source_directory/utils";

function ProfileCardSkeletonRow({ PrProfileCardSkeleton }) {
  return (
    <>
      <div style={{ gridRow: "span 4" }}>
        <PrProfileCardSkeleton />
      </div>
      <div className="d-none d-md-block" style={{ gridRow: "span 4" }}>
        <PrProfileCardSkeleton />
      </div>
      <div className="d-none d-xl-block" style={{ gridRow: "span 4" }}>
        <PrProfileCardSkeleton />
      </div>
    </>
  );
}

ProfileCardSkeletonRow.propTypes = {
  PrProfileCardSkeleton: PropTypes.elementType.isRequired,
};

function InfiniteHits({ PrProfileCardSkeleton, viewMode = "grid" }) {
  const { status } = useInstantSearch();
  const isLoading = status === "stalled";

  const { hits, isLastPage, showMore } = useInfiniteHits();
  const hasMore = !isLastPage;

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  useEffect(() => {
    if (lastTriggeredAt > 0) showMore();
  }, [lastTriggeredAt, showMore]);

  if (viewMode === "list") {
    // Reserve the trailing per-row action column (see PrCardList) so the column
    // headers line up with the row content.
    const isSignedIn = !!globalThis.Qwoted?.CURRENT_USER_ID;
    const actionColumnWidth = prListActionColumnWidth(isSignedIn);

    return (
      <>
        <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
        {hits.length > 0 && (
          <div className="sd-list-header-row" style={listHeaderRowStyle}>
            <span style={{ flex: "0 0 200px" }}>Name</span>
            <span style={{ flex: 1 }}>Covers</span>
            <span style={{ flex: "0 0 140px" }}>Represents</span>
            <span aria-hidden="true" style={{ flex: `0 0 ${actionColumnWidth}px` }} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {hits.map((hit) => (
            <PrCardList key={hit.objectID} hit={hit} />
          ))}
        </div>
        {hasMore && <div ref={scrollTriggerRef} />}
      </>
    );
  }

  return (
    <>
      <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
      <div className="sd-cards-grid">
        {isLoading && <ProfileCardSkeletonRow PrProfileCardSkeleton={PrProfileCardSkeleton} />}
        {!isLoading && hits.map((hit) => <PrCardGrid key={hit.objectID} hit={hit} />)}
      </div>
      {hasMore && (
        <div ref={scrollTriggerRef} className="sd-cards-grid sd-cards-grid--loading-more">
          <ProfileCardSkeletonRow PrProfileCardSkeleton={PrProfileCardSkeleton} />
        </div>
      )}
    </>
  );
}

InfiniteHits.propTypes = {
  PrProfileCardSkeleton: PropTypes.elementType,
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

export default InfiniteHits;
